import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { langNgheFirestore } from "../lib/langNgheFirestore";

const lichSuKhoCol = () => collection(db, "lichSuKho");

async function ganTenLienQuan(dsTho) {
  const maSotList = [...new Set(dsTho.map((ls) => ls.maSot).filter(Boolean))];
  const maNguoiList = [...new Set(dsTho.map((ls) => ls.nguoiThucHien).filter(Boolean))];

  const [sotDocs, nguoiDocs] = await Promise.all([
    Promise.all(maSotList.map((id) => getDoc(doc(db, "sot", id)))),
    Promise.all(maNguoiList.map((id) => getDoc(doc(db, "nguoiDung", id)))),
  ]);

  const maSotHienThi = Object.fromEntries(sotDocs.map((d) => [d.id, d.data()?.maSot]));
  const tenNguoi = Object.fromEntries(
    nguoiDocs.map((d) => [d.id, d.data()?.hoTen ?? d.data()?.email])
  );

  return dsTho.map((ls) => ({
    ...ls,
    maSotHienThi: maSotHienThi[ls.maSot] ?? null,
    tenNguoiThucHien: tenNguoi[ls.nguoiThucHien] ?? null,
  }));
}

function taoDuLieuGhi({
  loai,
  maSot,
  chuKyId = null,
  khoiLuongThuMua,
  khoiLuongCanLai = null,
  khoiLuongXuat = null,
  chenhLech = null,
}) {
  return {
    loai,
    maSot,
    chuKyId,
    khoiLuongThuMua: Number(khoiLuongThuMua),
    khoiLuongCanLai: khoiLuongCanLai != null ? Number(khoiLuongCanLai) : null,
    khoiLuongXuat: khoiLuongXuat != null ? Number(khoiLuongXuat) : null,
    chenhLech: chenhLech != null ? Number(chenhLech) : null,
    nguoiThucHien: auth.currentUser?.uid ?? null,
    thoiGian: new Date().toISOString(),
  };
}

export const lichSuKhoApi = {
  // 1 Sọt được tái sử dụng qua nhiều lượt nhập-xuất khác nhau (xem
  // sotApi.xuatKho) nên lọc lịch sử theo maSot không tách được đúng 1
  // cặp nhập/xuất — sinh sẵn 1 ID ngẫu nhiên (không ghi gì, chỉ mượn cơ chế
  // sinh ID của Firestore) để sotApi gắn chung vào cả dòng "nhap" lẫn
  // "xuat" của cùng 1 lượt, dùng làm khoá tra theoChuKy() bên dưới.
  sinhChuKyId() {
    return doc(lichSuKhoCol()).id;
  },

  // Ghi 1 dòng lịch sử bất biến — gọi ngay sau khi sotApi.nhapKho/xuatKho
  // cập nhật xong Sọt. Bắt buộc phải tách collection riêng vì xuatKho()
  // xoá sạch field nhapKhoLuc/khoiLuongCanLai trên chính Sọt để tái sử
  // dụng — không còn nơi nào khác giữ lại các lượt nhập/xuất trước đó.
  // `tx` tuỳ chọn: khi truyền vào (từ sotApi.nhapKho/xuatKho) ghi bằng
  // tx.set trong cùng transaction với cập nhật Sọt; không truyền thì addDoc
  // như trước (vẫn dùng được ngoài transaction).
  async ghi(payload, tx = null) {
    const data = taoDuLieuGhi(payload);
    if (tx) {
      tx.set(doc(lichSuKhoCol()), data);
      return;
    }
    await addDoc(lichSuKhoCol(), data);
  },

  // thoiGian lưu dạng ISO string nên range query lexicographic là chính xác,
  // không cần composite index — giống giaoDichApi.trongKhoang.
  async trongKhoang(tuNgay, denNgay) {
    const tu = `${tuNgay}T00:00:00.000Z`;
    const den = `${denNgay}T23:59:59.999Z`;
    const snap = await getDocs(
      query(lichSuKhoCol(), where("thoiGian", ">=", tu), where("thoiGian", "<=", den))
    );
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua);
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
  },

  langNgheTrongKhoang(tuNgay, denNgay, onChange, onError) {
    if (!tuNgay || !denNgay) {
      onChange([]);
      return () => {};
    }
    const tu = `${tuNgay}T00:00:00.000Z`;
    const den = `${denNgay}T23:59:59.999Z`;
    return langNgheFirestore(
      query(lichSuKhoCol(), where("thoiGian", ">=", tu), where("thoiGian", "<=", den)),
      {
        mapAsync: async (raw) => {
          const ganTen = await ganTenLienQuan(raw ?? []);
          return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
        },
        onChange,
        onError,
      }
    );
  },

  // Toàn bộ các dòng cùng 1 lượt nhập-xuất (thường là 1 "nhap" + 1 "xuat",
  // hoặc chỉ 1 "nhap" nếu Sọt còn đang nằm trong kho) — dùng cho popup
  // "Chi tiết chu kỳ" ở trang Lịch sử. Sắp tăng dần để nhập luôn hiện trước
  // xuất.
  async theoChuKy(chuKyId) {
    const snap = await getDocs(query(lichSuKhoCol(), where("chuKyId", "==", chuKyId)));
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua);
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? -1 : 1));
  },

  langNgheTheoChuKy(chuKyId, onChange, onError) {
    if (!chuKyId) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(lichSuKhoCol(), where("chuKyId", "==", chuKyId)), {
      mapAsync: async (raw) => {
        const ganTen = await ganTenLienQuan(raw ?? []);
        return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? -1 : 1));
      },
      onChange,
      onError,
    });
  },
};
